import type { IntlShape } from 'react-intl';

import { getI18nValidationMessage } from '../../forms-core/hooks/useFormFieldValidation.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../../forms-core/state/formfield-messages-state-reducer.js';
import {
  NativeValidityStateKeys,
  ValidationError,
} from '../../validation/types.js';
import { parseNumber } from './format.js';

/**
 * Sanitizes a numeric step value, returning 1 if the value is undefined, NaN, zero, or negative.
 * @internal
 */
export function sanitizePositiveNumber(value: number | undefined): number {
  return value !== undefined && !Number.isNaN(value) && value > 0 ? value : 1;
}

/**
 * Checks if the given value is aligned with the specified step and minimum value.
 * @internal
 */
export function isStepAligned(
  value: number,
  step: number,
  min: number = 0,
): boolean {
  step = sanitizePositiveNumber(step);
  const roundedValue = Math.round((value - min) / step) * step + min;
  const tolerance = step * 1e-9;
  return Math.abs(value - roundedValue) < tolerance;
}

/**
 * Determines the appropriate error key based on the provided value and validation constraints (min, max, step).
 * @internal
 */
export function getErrorKey(
  value: string | number,
  min?: number,
  max?: number,
  step?: number,
): keyof typeof NativeValidityStateKeys | null {
  const parsed = parseNumber(value);

  if (min !== undefined && parsed < min) {
    return 'rangeUnderflow';
  }

  if (max !== undefined && parsed > max) {
    return 'rangeOverflow';
  }

  if (step && step > 0 && !isStepAligned(parsed, step, min ?? 0)) {
    return 'stepMismatch';
  }
  return null;
}

/** @internal */
export function addError(
  errorKey: keyof typeof NativeValidityStateKeys,
  id: string,
  input: HTMLInputElement | HTMLSelectElement | null | undefined,
  dispatch: ((action: FormFieldMessagesReducerAction) => void) | undefined,
  intl: IntlShape,
  forceCustomValidity: boolean = false,
): void {
  if (forceCustomValidity) {
    dispatch?.({
      type: FormFieldMessagesAction.REMOVEMESSAGE,
      payload: { id, source: 'native' },
    });
  }

  dispatch?.({
    type: FormFieldMessagesAction.ADDMESSAGE,
    payload: {
      id,
      error: new ValidationError(
        getI18nValidationMessage(intl, errorKey, input),
        {
          source: 'native',
          validityStateKey: forceCustomValidity
            ? NativeValidityStateKeys.customError
            : NativeValidityStateKeys[errorKey],
          formControlId: input?.id,
        },
      ),
    },
  });
}
